import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProcessTimeline = ({ service }) => {
  const [activePhase, setActivePhase] = useState(0);

  const phases = service?.methodology || [
    {
      phase: "Discovery",
      description: "Requirements gathering and analysis",
      duration: "1-2 weeks",
      deliverables: ["Project scope", "Technical requirements", "Timeline estimate"]
    },
    {
      phase: "Design",
      description: "UI/UX design and architecture planning",
      duration: "2-3 weeks",
      deliverables: ["Wireframes", "System architecture", "Design mockups"]
    },
    {
      phase: "Development",
      description: "Core development and feature implementation",
      duration: "4-8 weeks",
      deliverables: ["MVP version", "Core features", "API integration"]
    },
    {
      phase: "Testing",
      description: "Quality assurance and bug fixes",
      duration: "1-2 weeks",
      deliverables: ["Test reports", "Bug fixes", "Performance optimization"]
    },
    {
      phase: "Deployment",
      description: "Production deployment and monitoring",
      duration: "1 week",
      deliverables: ["Live application", "Documentation", "Support handover"]
    }
  ];

  return (
    <div className="bg-background rounded-brand p-6">
      <h3 className="text-xl font-bold text-text-primary mb-6 flex items-center">
        <Icon name="GitBranch" size={24} className="mr-3 text-secondary" />
        Development Process
      </h3>

      {/* Timeline */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
        <div 
          className="absolute left-6 top-0 w-0.5 bg-secondary transition-all duration-1000"
          style={{ height: `${(activePhase + 1) * (100 / phases.length)}%` }}
        ></div>

        {/* Phase Items */}
        <div className="space-y-8">
          {phases?.map((phase, index) => (
            <div
              key={index}
              className={`relative flex items-start cursor-pointer transition-all duration-300 ${
                index <= activePhase ? 'opacity-100' : 'opacity-60'
              }`}
              onClick={() => setActivePhase(index)}
            >
              {/* Phase Indicator */}
              <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                index <= activePhase 
                  ? 'bg-secondary text-white' :'bg-muted text-text-secondary border-2 border-border'
              }`}>
                {index < activePhase ? (
                  <Icon name="Check" size={20} />
                ) : index === activePhase ? (
                  <Icon name="Play" size={20} />
                ) : (
                  <span className="text-sm font-bold">{index + 1}</span>
                )}
              </div>

              {/* Phase Content */}
              <div className="ml-6 flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className={`text-lg font-semibold transition-colors duration-300 ${
                    index <= activePhase ? 'text-text-primary' : 'text-text-secondary'
                  }`}>
                    {phase?.phase || `Phase ${index + 1}`}
                  </h4>
                  <span className="text-sm text-secondary font-medium">
                    {phase?.duration || 'TBD'}
                  </span>
                </div>
                
                <p className="text-text-secondary mb-3">{phase?.description || 'Description not available'}</p>

                {/* Deliverables */}
                {index <= activePhase && (
                  <div className="bg-muted rounded-brand p-4 animate-fadeIn">
                    <h5 className="text-sm font-semibold text-text-primary mb-2">
                      Key Deliverables:
                    </h5>
                    <ul className="space-y-1">
                      {(phase?.deliverables || []).map((deliverable, idx) => (
                        <li key={idx} className="flex items-center text-sm text-text-secondary">
                          <Icon name="CheckCircle" size={16} className="mr-2 text-success" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )) || []}
        </div>
      </div>

      {/* Progress Summary */}
      <div className="mt-8 p-4 bg-muted rounded-brand">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">Progress</span>
          <span className="text-sm text-secondary">
            {Math.round(((activePhase + 1) / phases.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-border rounded-full h-2">
          <div 
            className="bg-secondary h-2 rounded-full transition-all duration-1000"
            style={{ width: `${((activePhase + 1) / phases.length) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ProcessTimeline;